import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Color } from "config/colors";
import { FontFamily, FontSize } from "config/font";
import {
  DropdownOption,
  DropdownWithLabel,
} from "web-apps/company/components/general/dropDownWithLabel/dropdownWithLabel.component";
import { SettingsSection } from "./settingSection.component";

type Props = {
  value: number;
  onChange: (value: number) => void;
};

const MONTH_VALUE = "0";

const buildOptions = (
  monthLabel: string,
  dayLabel: (day: number) => string,
): DropdownOption[] => [
  { label: monthLabel, value: MONTH_VALUE },
  ...Array.from({ length: 11 }, (_, index) => {
    const value = String(index + 10);
    return { label: dayLabel(Number(value)), value };
  }),
];

const getExamplePeriod = (
  value: number,
  translate: (key: string, options?: Record<string, string | number>) => string,
): { description: string; title: string } => {
  if (value === 0) {
    return {
      title: translate("System.CompanyWorkPeriodDayCutOffMonthTitle"),
      description: translate("System.CompanyWorkPeriodDayCutOffHelpMonth"),
    };
  }

  return {
    title: translate("System.CompanyWorkPeriodDayCutOffDay", { day: value }),
    description: translate("System.CompanyWorkPeriodDayCutOffHelpDay", {
      day: value,
      startDay: value + 1,
      endDay: value,
    }),
  };
};

export const CompanyWorkPeriodDayCutOffSetting = (
  props: Props,
): JSX.Element => {
  const { t } = useTranslation();
  const options = useMemo(
    () =>
      buildOptions(t("System.CompanyWorkPeriodDayCutOffMonth"), (day) =>
        t("System.CompanyWorkPeriodDayCutOffDay", { day }),
      ),
    [t],
  );
  const selectedHelp = getExamplePeriod(props.value, t);

  return (
    <SettingsSection title={t("System.CompanyWorkPeriodDayCutOff")}>
      <DescriptionText>
        {t("System.CompanyWorkPeriodDayCutOffInfo")}
      </DescriptionText>
      <DropdownWithLabel
        label={t("System.CompanyWorkPeriodDayCutOffField")}
        options={options}
        value={String(props.value)}
        onChange={(value) => props.onChange(Number(value))}
        required
      />
      <SelectedHelpTitle>{selectedHelp.title}</SelectedHelpTitle>
      <SelectedHelpDescription>
        {selectedHelp.description}
      </SelectedHelpDescription>
    </SettingsSection>
  );
};

const DescriptionText = styled.p`
  color: ${Color.Black};
  font-family: ${FontFamily.MontserratRegular};
  font-size: ${FontSize.Standard};
  margin: 0 0 20px;
`;

const SelectedHelpTitle = styled.p`
  color: ${Color.Black};
  font-family: ${FontFamily.MontserratSemiBold};
  font-size: ${FontSize.Small};
  margin: 0 0 4px;
`;

const SelectedHelpDescription = styled.p`
  color: ${Color.Black};
  font-family: ${FontFamily.MontserratRegular};
  font-size: ${FontSize.Small};
  margin: 0;
`;
